import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import {
  ActivatedRoute,
  Params,
  QueryParamsHandling,
  Router,
} from "@angular/router";
import { from } from "rxjs";

import { InvalidOperationException } from "../exceptions/exceptions";
import {
  NavigationPage,
  QueryStringParameterKey,
} from "../routes/shared/shared-route.constant";

@Injectable({
  providedIn: "root",
})
export class RouterService {
  private readonly _window: Window;

  constructor(
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private _document: Document,
  ) {
    if (!this._document.defaultView) {
      throw new InvalidOperationException(
        "Was not able to find a global window object. This could be a side effect when changing to service side rendering (SSR)",
      );
    }

    this._window = this._document.defaultView;
  }

  setQueryParamsForCurrentRoute(queryStringParameters: QueryStringParameter[]) {
    const updatedParams: Params = {
      ...this._activatedRoute.snapshot.queryParams,
    };

    queryStringParameters.forEach((queryStringParameter) => {
      updatedParams[queryStringParameter.key] = queryStringParameter.value;
    });

    return from(this._router.navigate([], { queryParams: updatedParams }));
  }

  async tryNavigateToPage(
    navigationPage?: NavigationPage,
    queryStringParameters?: QueryStringParameter[],
  ) {
    if (navigationPage) {
      await this.navigateToPage(navigationPage, queryStringParameters);
    }
  }

  async navigateToPage(
    navigationPage: NavigationPage,
    queryStringParameters?: QueryStringParameter[],
  ) {
    const queryParamsHandling: QueryParamsHandling =
      navigationPage.queryParamsHandling ?? "merge";

    const queryParams: Params | null = {};

    if (queryStringParameters) {
      queryStringParameters.forEach((queryStringParameter) => {
        queryParams[queryStringParameter.key] = queryStringParameter.value;
      });
    }

    await this._router.navigate([navigationPage.pagePath], {
      queryParams: queryParams,
      queryParamsHandling: queryParamsHandling,
    });
  }

  navigateToUrl(url: string) {
    this._window.location.href = url;
  }

  tryGetQueryParam(queryStringParameterKey: QueryStringParameterKey) {
    const queryStringValue = this._activatedRoute.snapshot.queryParamMap.get(
      queryStringParameterKey,
    );

    return queryStringValue;
  }
}

export interface QueryStringParameter {
  key: QueryStringParameterKey;
  value: string | null;
}
